import axiosClient from "./axiosClient";

// Proxy GetFly qua uniapp-backend: POST /center/integration/:id/call {method,endpoint,payload}.
const CALL = "/center/integration/684da9ce05ded9ea98549675/call";
const DETAIL_FIELDS = 'account_code,account_name,birthday,gender,phone_office,email,relation_id,billing_address_street,created_at,contacts,detail_custom_fields';

// Cache định nghĩa custom field (options) — GetFly /custom_fields. Nạp 1 lần
// mỗi session vì gần như tĩnh. Map: { <name>: { htmltype, opts:{id:list_value} } }.
let _fieldDefs = null;
// Cache relation (giai đoạn CRM) — /crm/s/account_relations. Map {id: name}.
let _relations = null;

const isUnix = (v) => /^\d{9,10}$/.test(String(v));

// GetFly lưu field date/datetime ở unix mốc GIỜ VN → format theo
// Asia/Ho_Chi_Minh để không lệch 1 ngày (dùng Intl, không cần plugin dayjs-tz).
const fmtVnDate = (v) => new Date(Number(v) * 1000)
  .toLocaleDateString('en-GB', { timeZone: 'Asia/Ho_Chi_Minh' });
const fmtVnDateTime = (v) => {
  const d = new Date(Number(v) * 1000);
  const date = d.toLocaleDateString('en-GB', { timeZone: 'Asia/Ho_Chi_Minh' });
  const time = d.toLocaleTimeString('en-GB', { timeZone: 'Asia/Ho_Chi_Minh', hour12: false });
  return `${date} ${time.slice(0, 5)}`;
};

// Field kiểu `date` trên GetFly API mới nhận UNIX GIÂY (không nhận chuỗi
// dd/MM/yyyy như autosave cũ → 400). Chuẩn hoá ngày về unix giây, mốc 12:00
// giờ VN để phần NGÀY hiển thị đúng dù GetFly render theo UTC hay +07.
const toUnixDateSeconds = (input) => {
  if (input == null || input === '') return null;
  const s = String(input).trim();
  let iso = null;
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) iso = `${m[1]}-${m[2]}-${m[3]}T12:00:00+07:00`;
  if (!iso) {
    m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    if (m) iso = `${m[3]}-${m[2]}-${m[1]}T12:00:00+07:00`;
  }
  const t = iso ? new Date(iso).getTime() : new Date(s).getTime();
  return Number.isFinite(t) ? Math.floor(t / 1000) : null;
};

/**
 * API GetFly mới trả detail_custom_fields dạng GIÁ TRỊ THÔ (id option / unix).
 * Dựng `mapper` tương thích (mapper.<key>.{display_value,field_value}) + resolve:
 *   - select → nhãn (list_value) từ field defs
 *   - date   → DD/MM/YYYY (unix)
 */
function resolveDisplay(defs, key, v) {
  if (v == null || v === '') return '';
  const def = defs && defs[key];
  if (def && def.htmltype === 'select' && def.opts) {
    if (Array.isArray(v)) return v.map((x) => def.opts[String(x)] || x).filter(Boolean).join(', ');
    return def.opts[String(v)] || String(v);
  }
  if (def && def.htmltype === 'date' && isUnix(v)) return fmtVnDate(v);
  if (def && def.htmltype === 'datetime' && isUnix(v)) return fmtVnDateTime(v);
  // Fallback: field ngày không có def nhưng giá trị là unix.
  if (!def && /(^ngay_|_date$|het_han|deadline|birthday)/i.test(key) && isUnix(v)) {
    return fmtVnDate(v);
  }
  return String(v);
}

function detailToMapper(d, defs, relations) {
  const wrap = (k, v) => ({ display_value: resolveDisplay(defs, k, v), field_value: v == null ? '' : String(v) });
  const m = {};
  const STD = ['account_name', 'account_code', 'gender', 'phone_office', 'email', 'relation_id', 'billing_address_street'];
  for (const k of STD) m[k] = wrap(k, d?.[k]);
  m.birthday = wrap('birthday', d?.birthday);
  const cf = d?.detail_custom_fields || {};
  for (const [k, v] of Object.entries(cf)) m[k] = wrap(k, v);
  // relation_id (giai đoạn CRM) → tên relation.
  if (relations && d?.relation_id != null) {
    m.relation_id = {
      display_value: relations[String(d.relation_id)] || String(d.relation_id),
      field_value: String(d.relation_id),
    };
  }
  return m;
}

class IntegrationService {
  constructor() {
    this.integration = axiosClient;
  }

  // Nạp + cache định nghĩa custom field (module account) để resolve options.
  async loadFieldDefs() {
    if (_fieldDefs) return _fieldDefs;
    try {
      const res = await this.integration.post(CALL, {
        payload: {},
        method: 'GET',
        endpoint: `/custom_fields?fields=${encodeURIComponent('id,name,field_htmltype,custom_field_lists')}&filtering[module_key]=account&limit=500`,
      });
      const map = {};
      for (const f of (res?.data || [])) {
        const opts = {};
        for (const o of (f.custom_field_lists || [])) opts[String(o.id)] = o.list_value;
        if (f.name) map[f.name] = { htmltype: f.field_htmltype, opts };
      }
      _fieldDefs = map;
    } catch (_) {
      _fieldDefs = {}; // best-effort: không có defs thì hiện giá trị thô
    }
    return _fieldDefs;
  }

  // Nạp + cache relation (giai đoạn CRM) để resolve relation_id → tên.
  async loadRelations() {
    if (_relations) return _relations;
    try {
      const res = await this.integration.post(CALL, {
        payload: {},
        method: 'GET',
        endpoint: '/crm/s/account_relations?fields=id,relation_name,color_bg',
      });
      const map = {};
      for (const r of (res?.data || [])) map[String(r.id)] = r.relation_name;
      _relations = map;
    } catch (_) {
      _relations = {};
    }
    return _relations;
  }

  /**
   * Danh sách khoá học để dựng dropdown lọc — MỚI NHẤT trước.
   *
   * Dùng lại cache của `loadFieldDefs()` nên không tốn thêm lượt gọi nào.
   *
   * Sắp theo `id` giảm dần: id option tăng dần theo thứ tự khoá được tạo trên
   * GetFly, nên id lớn nhất là khoá mới nhất. Đo 27/09/2026: 259 option, id
   * 1→725, và 6 id lớn nhất đúng là 6 khoá đang tuyển
   * (725=C1-K30, 724=BTĐ-K38, 723=BSS-K38, 721=C1-K29, 720=BTĐ-K37, 719=BSS-K37).
   *
   * Cắt còn 25 vì danh sách đầy đủ có 259 mục, phần lớn là khoá đã đóng từ lâu
   * — cuộn qua chúng để tìm khoá hiện tại thì thà gõ tay còn nhanh hơn.
   */
  async loadCourseOptions(limit = 25) {
    const defs = await this.loadFieldDefs();
    const opts = (defs.khoa_hoc && defs.khoa_hoc.opts) || {};
    return Object.entries(opts)
      .map(([value, label]) => ({ value: Number(value), label }))
      .sort((a, b) => b.value - a.value)
      .slice(0, limit);
  }

  // Danh sách account — POST /crm/get/accounts (search + filtering).
  // Trả { accounts, data, columns } tương thích: account chuẩn hoá + columns
  // chứa options khoa_hoc (để grid hiện tên khoá thay vì ID).
  async queryCustomers(query = {}) {
    const search = query.search || query.account_name || undefined;

    /* Gom điều kiện lọc.
     *
     * Trước đây hàm này CHỈ đọc `query.filtering`, nên `account_type_multi` mà
     * HomePage gửi lên bị bỏ rơi im lặng — không lỗi, không cảnh báo, chỉ là
     * chọn nhóm xong không lọc gì cả.
     *
     * Tên trường đó cũng sai: GetFly không có `account_type_multi`, trường thật
     * là `account_type` (mảng). Gửi tên cũ — dù trong hay ngoài `filtering` —
     * GetFly đều trả **400**. Nên nếu chỉ chữa chỗ bỏ rơi mà giữ nguyên tên,
     * app sẽ đổi từ "im lặng không lọc" sang "hỏng ồn ào".
     *
     * `khoa_hoc` lưu ID OPTION dạng số chứ không phải nhãn: lọc bằng
     * `'BTĐ-K36'` trả 0 bản ghi, phải dùng `710`. Đo trên GetFly 27/09/2026.
     */
    const filtering = { ...(query.filtering || {}) };
    if (query.account_type != null && query.account_type !== '') {
      filtering.account_type = query.account_type;
    }
    if (query.khoa_hoc != null && query.khoa_hoc !== '') {
      filtering['custom_field.khoa_hoc:eq'] = query.khoa_hoc;
    }

    const [defs, res] = await Promise.all([
      this.loadFieldDefs(),
      this.integration.post(CALL, {
        method: 'POST',
        endpoint: '/crm/get/accounts',
        payload: {
          fields: 'account_name,account_code,birthday,phone_office,relation_id',
          custom_fields: 'ma_hoc_vien_theo_csdl,ma_khoa_csdl,can_cuoc_cong_dan,khoa_hoc,ngay_kham_suc_khoe,ket_qua_dat,kiem_tra_dat,dat_hoan_thanh',
          limit: query.limit || 50,
          offset: query.offset || 0,
          ...(search ? { search } : {}),
          filtering,
        },
      }),
    ]);
    const khoaOpts = (defs.khoa_hoc && defs.khoa_hoc.opts) || {};
    const rows = (Array.isArray(res?.data) ? res.data : []).map((a) => {
      const cf = a?.custom_fields && !Array.isArray(a.custom_fields) ? a.custom_fields : {};
      return {
        ...a,
        ...cf,
        account_id: a?.id != null ? String(a.id) : (a?.account_id || null),
        birthday: a?.birthday ? fmtVnDate(a.birthday) : '',
        khoa_hoc_label: khoaOpts[String(cf.khoa_hoc)] || cf.khoa_hoc || '',
      };
    });
    const columns = [{
      key: 'khoa_hoc',
      options: Object.entries(khoaOpts).map(([value, label]) => ({ value: Number(value), label })),
    }];
    return { accounts: rows, data: rows, columns };
  }

  // Chi tiết account — GET /crm/accounts/{id}. Trả {account_id, mapper} đã resolve.
  async getCustomerDetail(customerId) {
    const [defs, relations, res] = await Promise.all([
      this.loadFieldDefs(),
      this.loadRelations(),
      this.integration.post(CALL, {
        payload: {},
        method: 'GET',
        endpoint: `/crm/accounts/${customerId}?fields=${encodeURIComponent(DETAIL_FIELDS)}`,
      }),
    ]);
    return { account_id: customerId, mapper: detailToMapper(res, defs, relations) };
  }

  // Danh sách task — GET /tasks (filtering query-param).
  async queryTasks(query = {}) {
    const params = new URLSearchParams({
      fields: 'id,task_name,project_id,task_status,task_status_title,task_receiver,task_receiver_display_name,task_start_date,task_end_date',
      limit: String(query.limit || 30),
      offset: String(query.offset || 0),
      'filtering[project_id]': String(query.project_id || 64),
    });
    if (query.nature_status) params.set('filtering[nature_status]', query.nature_status);
    return this.integration.post(CALL, {
      payload: {},
      method: 'GET',
      endpoint: `/tasks?${params.toString()}`,
    });
  }

  // Ghi field — PUT /crm/accounts/{id} {is_quick_update:1, custom_fields}.
  async updateAccountFields(customerId, customFields) {
    return this.integration.post(CALL, {
      method: 'PUT',
      endpoint: `/crm/accounts/${customerId}`,
      payload: { is_quick_update: 1, custom_fields: customFields },
    });
  }

  // Đăng ký thi lại hạng A1/A — 1 PUT nhiều custom_fields.
  async registerRetestA1A(customerId, retestData = {}) {
    await this.updateAccountFields(customerId, {
      a1_a_dang_ky_thi_lai: '523',
      a1_a_sdt: retestData.phone,
      a1_a_nguoi_nop_ho_so: retestData.applicant,
      a1_a_ngay_nop_tien_thi_lai: toUnixDateSeconds(retestData.paymentDate),
      ...(retestData.retestType ? { a1_a_hang_muc_dang_ky_thi_lai: retestData.retestType } : {}),
    });
  }

  // Đăng ký thi lại hạng B/C — 1 PUT nhiều custom_fields.
  async registerRetestBC(customerId, retestData = {}) {
    await this.updateAccountFields(customerId, {
      oto_dang_ky_thi_lai: retestData.retestCount,
      oto_ngay_nop_tien_thi_lai: toUnixDateSeconds(retestData.paymentDate),
      oto_nguoi_dang_ky_thi_lai: retestData.applicant,
      oto_sdt_nguoi_dang_ky_tl: retestData.phone,
      oto_hang_muc_dang_ky_thi_lai: retestData.retestText,
    });
  }
}

const IntegrationServiceInstance = new IntegrationService();
export default IntegrationServiceInstance;
